import React from 'react';
import {
  arcGradient,
  arcPath,
  CORE_STOPS,
  coreGradient,
  FLARE_GLOW_STOPS,
  FLARE_STOPS,
  FULL,
  HALO_STOPS,
  orbitPoint,
  PLANET_HALO_STOPS,
  PLANET_STOPS,
  planetGradient,
  SMALL,
  spindlePath,
  type Stop,
} from './brand-geometry.js';

/**
 * Inline SVG icon set for the navigation shell.
 *
 * No icon font / CDN: every glyph ships as a small stroke-based path so the
 * bundle stays self-contained and TV browsers don't pay for a webfont fetch.
 */

type IconProps = { className?: string };

function strokeIcon(children: React.ReactNode): React.ComponentType<IconProps> {
  return function StrokeIcon({ className }: IconProps): React.JSX.Element {
    return (
      <svg
        className={className}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        {children}
      </svg>
    );
  };
}

export const HomeIcon = strokeIcon(
  <path d="M3 10.5 12 3l9 7.5M5 9.5V21h5v-6h4v6h5V9.5" />,
);

export const MoviesIcon = strokeIcon(
  <>
    <rect x="3" y="4.5" width="18" height="15" rx="2" />
    <path d="M7 4.5v15M17 4.5v15M3 9.5h4M17 9.5h4M3 14.5h4M17 14.5h4" />
  </>,
);

export const TvIcon = strokeIcon(
  <>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="m8 6 4-3 4 3M9 22h6" />
  </>,
);

/**
 * Закладка, а не сердце.
 *
 * Сердце читается как «нравится» — оценка, которой у нас нет. Здесь же речь про
 * «отложил к себе», и знак закладки говорит это без подписи: то же движение,
 * что в браузере и в читалке.
 */
export const FavoritesIcon = strokeIcon(
  <path d="M6.5 3.5h11a1 1 0 0 1 1 1V21l-6.5-4.2L5.5 21V4.5a1 1 0 0 1 1-1Z" />,
);

/**
 * Та же закладка, но закрашенная — для состояния «уже отложено».
 *
 * Разница именно в заливке, а не в цвете или подписи: контур и заливка
 * различаются с любого расстояния и не требуют читать кнопку.
 */
export function FavoritesIconFilled({ className }: IconProps): React.JSX.Element {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M6.5 3.5h11a1 1 0 0 1 1 1V21l-6.5-4.2L5.5 21V4.5a1 1 0 0 1 1-1Z" />
    </svg>
  );
}

export const ContinueIcon = strokeIcon(
  <>
    <path d="M3.5 12a8.5 8.5 0 1 0 2.8-6.3" />
    <path d="M3 3v5h5M12 8v4.5l3.2 1.8" />
  </>,
);

/** Три полосы: общепринятый знак «показать меню». */
export const MenuIcon = strokeIcon(
  <>
    <path d="M4 7h16" />
    <path d="M4 12h16" />
    <path d="M4 17h16" />
  </>,
);

export const SettingsIcon = strokeIcon(
  <>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M19.4 13.4a7.7 7.7 0 0 0 0-2.8l2-1.5-2-3.4-2.3.7a7.7 7.7 0 0 0-2.5-1.4L14 2.5h-4l-.5 2.5a7.7 7.7 0 0 0-2.5 1.4l-2.3-.7-2 3.4 2 1.5a7.7 7.7 0 0 0 0 2.8l-2 1.5 2 3.4 2.3-.7a7.7 7.7 0 0 0 2.5 1.4l.5 2.5h4l.5-2.5a7.7 7.7 0 0 0 2.5-1.4l2.3.7 2-3.4-2-1.5Z" />
  </>,
);

/** Координаты в разметке — до сотых: длинные хвосты дробей только раздувают DOM. */
export const round2 = (v: number): number => Math.round(v * 100) / 100;

/** Ступени градиента для `<radialGradient>`/`<linearGradient>`. */
export function GradientStops({ stops }: { stops: readonly Stop[] }): React.JSX.Element {
  return (
    <>
      {stops.map(([offset, color, opacity]) => (
        <stop key={offset} offset={round2(offset)} stopColor={color} stopOpacity={opacity} />
      ))}
    </>
  );
}

/** Радиальный градиент в координатах поля (userSpaceOnUse), а не фигуры. */
export function RadialPaint({
  id,
  at,
  stops,
}: {
  id: string;
  at: { cx: number; cy: number; r: number };
  stops: readonly Stop[];
}): React.JSX.Element {
  return (
    <radialGradient id={id} gradientUnits="userSpaceOnUse" cx={round2(at.cx)} cy={round2(at.cy)} r={round2(at.r)}>
      <GradientStops stops={stops} />
    </radialGradient>
  );
}

export function LinearPaint({
  id,
  from,
  to,
  stops,
}: {
  id: string;
  from: readonly [number, number];
  to: readonly [number, number];
  stops: readonly Stop[];
}): React.JSX.Element {
  return (
    <linearGradient
      id={id}
      gradientUnits="userSpaceOnUse"
      x1={round2(from[0])}
      y1={round2(from[1])}
      x2={round2(to[0])}
      y2={round2(to[1])}
    >
      <GradientStops stops={stops} />
    </linearGradient>
  );
}

/**
 * Идентификатор градиента, уникальный для экземпляра.
 *
 * Общий `id` на странице ломается тихо: `url(#…)` берёт первый элемент с этим
 * именем, и если его SVG скрыт (`display: none` — свёрнутый ряд, закрытое
 * меню), браузер не рисует градиент НИ У ОДНОГО знака. Двоеточия и кавычки
 * из `useId` убираем — не все TV-браузеры их переваривают внутри `url()`.
 */
export function useSvgId(prefix: string): string {
  return `${prefix}${React.useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
}

/** Обе дуги и обе планеты: вторая — та же, повёрнутая на пол-оборота. */
const TURNS = [0, 180] as const;

/** Фигуры полного знака, посчитанные один раз на модуль. */
const FULL_SHAPES = TURNS.map((turn) => {
  const head = FULL.head + turn;
  const tail = FULL.tail + turn;
  const at = FULL.planetAt + turn;
  const [px, py] = orbitPoint(at, FULL.orbit);
  return {
    arc: arcPath(head, tail, FULL.orbit),
    gradient: arcGradient(head, tail, at, FULL.orbit, false),
    planet: { cx: px, cy: py },
  };
});

const SMALL_SHAPES = TURNS.map((turn) => {
  const head = SMALL.head + turn;
  const tail = SMALL.tail + turn;
  const at = SMALL.planetAt + turn;
  const [px, py] = orbitPoint(at, SMALL.orbit);
  return {
    arc: arcPath(head, tail, SMALL.orbit),
    gradient: arcGradient(head, tail, at, SMALL.orbit, true),
    planet: { cx: px, cy: py },
  };
});

const FLARE_PATH = spindlePath(50, 50, FULL.flare, FULL.flareW);
const FLARE_GLOW_PATH = spindlePath(50, 50, FULL.flareGlow, FULL.flareGlowW);

/**
 * Знак Lumos: светящееся ядро-солнце с анаморфным бликом, вокруг — наклонная
 * орбита из двух дуг и две планеты.
 *
 * Поле знака (viewBox) — орбита с планетами; ореол и блик выходят за него
 * (`overflow: visible` в стилях), поэтому рядом со словом знак стоит по
 * размеру орбиты, а свет ложится на фон вокруг.
 *
 * Вариантов два, и оба лежат в разметке — выбирает CSS по месту (`.is-simple`,
 * `.brand-mark`, …): полный (ореолы, блик, тонкие дуги с гаснущими хвостами —
 * от 64 px) и упрощённый (без ореолов и блика, дуги и планеты толще — для
 * 16–48 px, где тонкая нить рассыпалась бы в пыль). Геометрия — в
 * `brand-geometry.ts`.
 */
export function LumosMark({ className, simple = false }: IconProps & { simple?: boolean }): React.JSX.Element {
  const id = useSvgId('lm');
  const classes = ['lumos-mark', simple ? 'is-simple' : '', className ?? ''].filter(Boolean).join(' ');
  return (
    <svg className={classes} viewBox="0 0 100 100" aria-hidden="true" focusable="false">
      <defs>
        <RadialPaint id={`${id}h`} at={{ cx: 50, cy: 50, r: FULL.halo }} stops={HALO_STOPS} />
        <RadialPaint id={`${id}c`} at={coreGradient(FULL.core)} stops={CORE_STOPS} />
        <RadialPaint id={`${id}s`} at={coreGradient(SMALL.core)} stops={CORE_STOPS} />
        <LinearPaint id={`${id}f`} from={[50 - FULL.flare, 50]} to={[50 + FULL.flare, 50]} stops={FLARE_STOPS} />
        <LinearPaint
          id={`${id}g`}
          from={[50 - FULL.flareGlow, 50]}
          to={[50 + FULL.flareGlow, 50]}
          stops={FLARE_GLOW_STOPS}
        />
        {FULL_SHAPES.map(({ gradient, planet }, i) => (
          <React.Fragment key={i}>
            <LinearPaint id={`${id}a${i}`} from={gradient.from} to={gradient.to} stops={gradient.stops} />
            <LinearPaint
              id={`${id}ag${i}`}
              from={gradient.from}
              to={gradient.to}
              stops={gradient.stops.map(([o, c, a]) => [o, c, a * 0.12] as const)}
            />
            <RadialPaint id={`${id}ph${i}`} at={{ ...planet, r: FULL.planetHalo }} stops={PLANET_HALO_STOPS} />
            <RadialPaint id={`${id}p${i}`} at={planetGradient(FULL.planet, planet.cx, planet.cy)} stops={PLANET_STOPS} />
          </React.Fragment>
        ))}
        {SMALL_SHAPES.map(({ gradient, planet }, i) => (
          <React.Fragment key={i}>
            <LinearPaint id={`${id}sa${i}`} from={gradient.from} to={gradient.to} stops={gradient.stops} />
            <RadialPaint
              id={`${id}sp${i}`}
              at={planetGradient(SMALL.planet, planet.cx, planet.cy)}
              stops={PLANET_STOPS}
            />
          </React.Fragment>
        ))}
      </defs>

      <g className="lm-full">
        <circle cx="50" cy="50" r={FULL.halo} fill={`url(#${id}h)`} />
        {FULL_SHAPES.map(({ arc }, i) => (
          <React.Fragment key={i}>
            <path d={arc} fill="none" stroke={`url(#${id}ag${i})`} strokeWidth={FULL.arcGlow} strokeLinecap="round" />
            <path d={arc} fill="none" stroke={`url(#${id}a${i})`} strokeWidth={FULL.arc} strokeLinecap="round" />
          </React.Fragment>
        ))}
        {FULL_SHAPES.map(({ planet }, i) => (
          <React.Fragment key={i}>
            <circle cx={round2(planet.cx)} cy={round2(planet.cy)} r={FULL.planetHalo} fill={`url(#${id}ph${i})`} />
            <circle cx={round2(planet.cx)} cy={round2(planet.cy)} r={FULL.planet} fill={`url(#${id}p${i})`} />
          </React.Fragment>
        ))}
        <path d={FLARE_GLOW_PATH} fill={`url(#${id}g)`} />
        <path d={FLARE_PATH} fill={`url(#${id}f)`} />
        <circle cx="50" cy="50" r={FULL.core} fill={`url(#${id}c)`} />
      </g>

      <g className="lm-simple">
        {SMALL_SHAPES.map(({ arc, planet }, i) => (
          <React.Fragment key={i}>
            <path d={arc} fill="none" stroke={`url(#${id}sa${i})`} strokeWidth={SMALL.arc} strokeLinecap="round" />
            <circle cx={round2(planet.cx)} cy={round2(planet.cy)} r={SMALL.planet} fill={`url(#${id}sp${i})`} />
          </React.Fragment>
        ))}
        <circle cx="50" cy="50" r={SMALL.core} fill={`url(#${id}s)`} />
      </g>
    </svg>
  );
}

/**
 * Треугольник «пуск» — заливкой, а не контуром: на янтарной кнопке контур
 * толщиной в полтора пикселя рябит, а сплошной знак читается с дивана.
 */
export function PlayIcon({ className }: IconProps): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d="M7.5 4.8v14.4c0 .8.9 1.3 1.6.9l11.3-7.2c.6-.4.6-1.3 0-1.7L9.1 3.9c-.7-.4-1.6.1-1.6.9Z" />
    </svg>
  );
}

export const SearchIcon = strokeIcon(
  <>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m20 20-4.4-4.4" />
  </>,
);

export const BackIcon = strokeIcon(<path d="M14.5 5.5 8 12l6.5 6.5" />);

export const UserIcon = strokeIcon(
  <>
    <circle cx="12" cy="8.5" r="3.8" />
    <path d="M4.8 20.2c1.2-3.6 3.9-5.4 7.2-5.4s6 1.8 7.2 5.4" />
  </>,
);

export const LockIcon = strokeIcon(
  <>
    <rect x="5" y="10.5" width="14" height="10" rx="2.2" />
    <path d="M8.5 10.5V7.8a3.5 3.5 0 0 1 7 0v2.7M12 14.6v2.2" />
  </>,
);

/** Эфир: экран с волнами — прямой эфир, а не ещё один «сериал». */
export const LiveIcon = strokeIcon(
  <>
    <rect x="3" y="7" width="18" height="12.5" rx="2" />
    <path d="m8.5 3 3.5 4 3.5-4" />
    <circle cx="12" cy="13.2" r="1.4" />
    <path d="M8.8 10.4a4.4 4.4 0 0 0 0 5.6M15.2 10.4a4.4 4.4 0 0 1 0 5.6" />
  </>,
);
